import React, { useState } from 'react';

import Icon from 'components/elements/Icon';
import {
  OverlayBaseForm,
  OverlayFooter,
  OverlayPortal,
  OverlayTitle,
  OverlayWrapper,
} from 'shared/Overlay';

// PLA-1066: `actorKind` distingue tres cosas que no son lo mismo en una pantalla de
// trazabilidad: lo hizo una persona que conocemos, lo hizo el sistema, o lo hizo una persona
// pero no quedó registrado quién.
type ActorKind = 'user' | 'system' | 'unknown';

export interface ContractHistoryEvent {
  at: string;
  kind: string;
  actor: string | null;
  actorKind: ActorKind;
  title: string;
  detail: string | null;
  versionNumber: number | null;
}

// PLA-1130: PDF que esperan firma en Adobe; vacío fuera de `signature_requested`.
export interface ContractSentDocument {
  label: string;
  url: string;
}

export interface ContractHistoryProps {
  events: ContractHistoryEvent[];
  sentDocuments?: ContractSentDocument[];
}

const Actor = ({
  actor,
  actorKind,
}: Pick<ContractHistoryEvent, 'actor' | 'actorKind'>) => {
  if (actor) {
    return <>{actor}</>;
  }

  if (actorKind === 'unknown') {
    return (
      <em className='contract-history__actor--unknown'>
        {I18n.translate('contracts.history.unknown_actor')}
      </em>
    );
  }

  return <em>{I18n.translate('contracts.history.system')}</em>;
};

const ContractHistory = ({ events, sentDocuments = [] }: ContractHistoryProps) => {
  const [isOpen, setOpen] = useState(false);

  return (
    <div className='contract-history'>
      {sentDocuments.map((document) => (
        <a
          key={document.url}
          href={document.url}
          target='_blank'
          rel='noopener noreferrer'
          className='button button--with-blue-border contract-history__button'
          data-cy='contract-sent-document'
        >
          <Icon icon='document' className='contract-history__icon' />
          {document.label}
        </a>
      ))}

      <button
        type='button'
        className='button button--with-blue-border contract-history__button'
        onClick={() => setOpen(true)}
        data-cy='contract-history'
      >
        <Icon icon='log' className='contract-history__icon' />
        {I18n.translate('contracts.history.button')}
      </button>

      {isOpen && (
        <OverlayPortal>
          <OverlayWrapper>
            <OverlayBaseForm>
              <OverlayTitle>{I18n.translate('contracts.history.title')}</OverlayTitle>

              {events.length === 0 ? (
                <p className='contract-history__empty'>
                  {I18n.translate('contracts.history.empty')}
                </p>
              ) : (
                <ol className='contract-history__timeline'>
                  {events.map((event, index) => (
                    <li
                      key={index}
                      className={`contract-history__event contract-history__event--${event.kind}`}
                    >
                      <div className='contract-history__marker' />
                      <div className='contract-history__body'>
                        <div className='contract-history__head'>
                          <span className='contract-history__title'>{event.title}</span>
                          {event.versionNumber && (
                            <span className='contract-history__version'>
                              v{event.versionNumber}
                            </span>
                          )}
                        </div>
                        <div className='contract-history__meta'>
                          <span className='contract-history__date'>{event.at}</span>
                          <span className='contract-history__actor'>
                            <Actor actor={event.actor} actorKind={event.actorKind} />
                          </span>
                        </div>
                        {event.detail && (
                          <p className='contract-history__detail'>{event.detail}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}

              <OverlayFooter>
                <button
                  type='button'
                  className='button button--gray'
                  onClick={() => setOpen(false)}
                  data-cy='contract-history-close'
                >
                  {I18n.translate('buttons.close')}
                </button>
              </OverlayFooter>
            </OverlayBaseForm>
          </OverlayWrapper>
        </OverlayPortal>
      )}
    </div>
  );
};

export default ContractHistory;
